/* pages/Services.jsx (FIXED: Long classNames split into clean multi-line template literals. No more JSX parser errors!) */
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function Services() {
return (
    <>
      <Helmet>
        <title>Services | Our Financial Success - Personalized Plans</title>
        <meta name="description" content="Individual & corporate planning: savings, retirement, insurance, emergency funds, and more for Canadian families & businesses." />
      </Helmet>

      {/* Hero */}
      {/* Hero - FIXED: Darker overlay + text shadows for 100% visibility */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="min-h-[50vh] bg-cover bg-center bg-no-repeat flex items-center py-20 px-6 relative overflow-hidden"
        style={{
          backgroundImage: `
            linear-gradient(135deg, rgba(16,185,129,0.85), rgba(120,53,15,0.75)), 
            url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&fit=crop&w=2000&q=85')
          `.trim(),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        variants={fadeInUp}
      >
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-2xl text-white/95" 
              style={{ 
                textShadow: '0 4px 12px rgba(0,0,0,0.6), 0 0 20px rgba(255,255,255,0.3)' 
              }}>
            Personalized <span className="text-amber-200/95 drop-shadow-xl">Financial Plans</span>
          </h1>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed text-white/90 px-4" 
            style={{ 
              textShadow: '0 2px 8px rgba(0,0,0,0.5)' 
            }}>
            Tailored strategies for individuals and corporations. Government-licensed excellence across Canada.
          </p>
        </div>
      </motion.section>

      <div className="max-w-6xl mx-auto py-32 px-6">
        {/* Abundance Tagline */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-24 py-12 bg-gradient-to-r from-emerald-50 to-amber-50 rounded-3xl shadow-2xl"
          variants={fadeInUp}
          style={{ border: '3px solid rgba(16, 185, 129, 0.2)' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800 bg-gradient-to-r from-emerald-600 to-amber-400 bg-clip-text text-transparent">
            Abundant prosperity flows in every scenario
          </h2>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-700 font-medium leading-relaxed">
            We've unlocked financial harmony for thousands of Canadian families and businesses.
          </p>
        </motion.div>


        {/* Individual */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-32"
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-20 text-gray-800 bg-gradient-to-r from-emerald-600 to-amber-400 bg-clip-text text-transparent">
            Individual Planning
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="space-y-4 text-xl p-8"
            >
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Personal Savings</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Emergency Wealth Reserve</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Retirement Planning</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Automatic Annuity Setup</span>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              variants={fadeInUp}
              className="space-y-4 text-xl p-8"
            >
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Investment with Insurance & Growth</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Family Safety Planning</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl font-bold text-emerald-600 min-w-[1.5rem]">✓</span>
                <span>Education & Kids Savings</span>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Corporate - FIXED: All long classNames now multi-line template literals (`.trim()`) for JSX parser safety */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-32"
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-20 text-gray-800 bg-gradient-to-r from-emerald-600 to-amber-400 bg-clip-text text-transparent">
            Corporate Planning
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className={`
                p-10
                bg-gradient-to-br
                from-emerald-50
                to-emerald-100
                rounded-3xl
                shadow-xl
                hover:shadow-2xl
                transition-all
                duration-300
                border
                border-emerald-100
              `.trim()}
            >
              <h3 className="text-2xl font-bold mb-6 text-emerald-800 flex items-center gap-3">
                🛡️ Business Safety
              </h3>
              <ul className="space-y-3 text-lg text-emerald-800">
                <li className="flex items-center gap-2">✓ All-aspect protection</li>
                <li className="flex items-center gap-2">✓ Risk management</li>
                <li className="flex items-center gap-2">✓ Corporate Emergency Reserves</li>
              </ul>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              variants={fadeInUp}
              className={`
                p-10
                bg-gradient-to-br
                from-amber-50
                to-amber-100
                rounded-3xl
                shadow-xl
                hover:shadow-2xl
                transition-all
                duration-300
                border
                border-amber-200
              `.trim()}
            >
              <h3 className="text-2xl font-bold mb-6 text-amber-700 flex items-center gap-3">
                🏥 Insurance Services
              </h3>
              <ul className="space-y-3 text-lg text-amber-700">
                <li className="flex items-center gap-2">✓ Comprehensive coverage</li>
                <li className="flex items-center gap-2">✓ Tailored policies</li>
              </ul>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              variants={fadeInUp}
              className={`
                p-10
                bg-gradient-to-br
                from-amber-100
                to-amber-200
                rounded-3xl
                shadow-xl
                hover:shadow-2xl
                transition-all
                duration-300
                border
                border-amber-300
              `.trim()}
            >
              <h3 className="text-2xl font-bold mb-6 text-amber-800 flex items-center gap-3">
                💰 Loan Support
              </h3>
              <ul className="space-y-3 text-lg text-amber-800">
                <li className="flex items-center gap-2">✓ Financing guidance</li>
                <li className="flex items-center gap-2">✓ Growth funding</li>
              </ul>
            </motion.div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center"
          variants={fadeInUp}
        >
          <h3 className="text-3xl font-bold mb-8 text-gray-800">
            Ready for Your Custom Plan?
          </h3>
          <Link to="/contact" className="btn-primary text-xl px-12 py-6 inline-block shadow-2xl hover:shadow-3xl">
            Get Personalized Quote
          </Link>
        </motion.div>
      </div>
    </>
  );
}